import { ReactNode, useEffect, useState } from 'react';
import { FileField, Image, ImageField } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';

import { icon_pause, icon_play } from '@gn-audio/icons';
import { JabraIcon, useFallbackTranslation } from '@gn-audio/jss-components';

import { useLazyVideo } from 'common/hooks/useLazyVideo';
import { useVideoSource } from 'utils/hooks/useVideoSource';

type VideoBackgroundProps = {
  videoLg: FileField;
  videoMd: FileField;
  videoXs: FileField;
  image: ImageField;
  backgroundContrast: number;
  reducedWidth: boolean;
  unlockAspectRatio?: boolean;
  hideControls?: boolean;
  playerOpened?: boolean;
  children: ReactNode;
  videoContainerClass?: string;
  hideControlsOnMobile?: boolean;
};

const VideoBackground = ({
  videoLg,
  videoMd,
  videoXs,
  image,
  backgroundContrast,
  reducedWidth,
  hideControls = false,
  unlockAspectRatio = false,
  playerOpened = false,
  videoContainerClass = 'aspect-video',
  hideControlsOnMobile = false,
  children,
}: VideoBackgroundProps) => {
  const { t } = useFallbackTranslation();
  const [isPaused, setIsPaused] = useState(false);

  const videoSrcXs = useVideoSource([videoXs, videoMd, videoLg]);
  const videoSrcMd = useVideoSource([videoMd, videoLg, videoXs]);
  const videoSrcLg = useVideoSource([videoLg, videoMd, videoXs]);

  const hasVideoSrc = videoSrcXs?.src || videoSrcMd?.src || videoSrcLg?.src;

  const { videoRef, containerRef: videoContainerRef, isVideoLoaded } = useLazyVideo();

  const posterImage = image?.value?.src || '';

  useEffect(() => {
    const video = videoRef.current;
    if (video && isVideoLoaded) {
      if (playerOpened) {
        video.pause();
        setIsPaused(true);
      } else if (!isPaused) {
        void video.play();
      }
    }
  }, [playerOpened, isVideoLoaded, isPaused, videoRef]);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (video && isVideoLoaded) {
      if (isPaused) {
        void video.play();
      } else {
        video.pause();
      }
      setIsPaused(!isPaused);
    }
  };

  return (
    <div className={classNames(reducedWidth && 'container')}>
      <div
        ref={videoContainerRef}
        className={classNames('relative w-full', !unlockAspectRatio && videoContainerClass)}
      >
        {!isVideoLoaded && (
          <div className="absolute left-0 top-0 h-full w-full">
            {image && (
              <Image
                field={image}
                className={classNames('h-full w-full object-cover', reducedWidth && 'rounded-3xl')}
              />
            )}
          </div>
        )}
        {hasVideoSrc && (
          <video
            preload="none"
            ref={videoRef}
            className={classNames(
              'lazy h-full w-full object-cover',
              reducedWidth && 'rounded-3xl',
              unlockAspectRatio ? 'h-auto w-full' : 'absolute left-0 top-0 h-full w-full'
            )}
            loop
            playsInline
            autoPlay
            muted
            poster={posterImage}
          >
            {videoSrcLg && (
              <source
                data-src={videoSrcLg.src}
                type={videoSrcLg.mimeType}
                media="(min-width: 1281px)"
              />
            )}
            {videoSrcMd && (
              <source
                data-src={videoSrcMd.src}
                type={videoSrcMd.mimeType}
                media="(min-width: 641px)"
              />
            )}
            {videoSrcXs && <source data-src={videoSrcXs.src} type={videoSrcXs.mimeType} />}
          </video>
        )}
        <div
          className={classNames(
            'flex min-h-full w-full !max-w-none flex-col items-center justify-center',
            reducedWidth && 'rounded-3xl',
            unlockAspectRatio ? 'absolute inset-0 z-10' : 'relative'
          )}
          style={{
            backgroundColor: `rgba(0, 0, 0, ${(backgroundContrast ?? 40) / 100})`,
          }}
        >
          {children}
          {hasVideoSrc && !hideControls && (
            <button
              aria-label={isPaused ? t('system.Play') : t('system.Pause')}
              className={classNames(
                'absolute bottom-6 left-6 z-10 h-12 w-12 items-center justify-center',
                hideControlsOnMobile ? 'hidden md:flex' : 'flex',
                'md:right-auto md:top-auto lg:bottom-9 lg:left-9'
              )}
              onClick={togglePlayPause}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-solid border-white text-white transition-colors duration-300 ease-linear hover:bg-white hover:text-black">
                {isPaused ? (
                  <JabraIcon viewBox="0 0 24 24" className="h-6 w-6" Icon={icon_play} />
                ) : (
                  <JabraIcon viewBox="0 0 24 24" className="h-6 w-6" Icon={icon_pause} />
                )}
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoBackground;
